import { useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Clock, Leaf, Sparkles, Sun, Moon, Check, ArrowRight, ShieldCheck,
  HeartHandshake, Star, ChevronDown, Lock, CreditCard,
} from "lucide-react";
import Logo from "../components/Logo";
import PaypalCheckout from "../components/PaypalCheckout";
import { IMAGES } from "../lib/images";
import api, { formatApiErrorDetail } from "../lib/api";

const PRICE = "47";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } }),
};

const benefits = [
  { icon: Clock, title: "Solo 8 min al día", text: "Diseñado para mujeres ocupadas. Un ritual breve que cabe en tu mañana o tu noche." },
  { icon: Leaf, title: "100% natural", text: "Ejercicios de yoga facial y productos naturales. Sin agujas, sin químicos agresivos." },
  { icon: Sparkles, title: "Resultados visibles", text: "Tonifica, reafirma y ilumina. Un rostro más luminoso y descansado semana a semana." },
  { icon: Sun, title: "Rutina mañana y noche", text: "Te acompaño paso a paso en tu ritual facial de día y de noche, con mis productos favoritos." },
];

const weeks = [
  { n: 1, theme: "Despertar y Relajar", focus: "Frente, entrecejo y relajación" },
  { n: 2, theme: "Mirada Radiante", focus: "Contorno de ojos" },
  { n: 3, theme: "Pómulos y Mejillas", focus: "Lifting de la zona media" },
  { n: 4, theme: "Óvalo y Cuello", focus: "Definición de mandíbula y cuello" },
];

const testimonials = [
  { name: "Carmen, 47", text: "En tres semanas noté mi rostro más firme y descansado. Y lo mejor: solo me lleva unos minutos al día." },
  { name: "Elena, 42", text: "Por fin una rutina que puedo mantener. Los ejercicios son fáciles y las recomendaciones de productos, buenísimas." },
  { name: "Marta, 51", text: "Mi piel nunca había estado tan luminosa. La combinación de yoga facial y rutina natural es perfecta." },
];

const faqs = [
  { q: "¿Cuánto tiempo necesito al día?", a: "Entre 6 y 10 minutos. El programa está pensado para mujeres sin tiempo: rituales breves y fáciles de mantener." },
  { q: "¿Necesito comprar productos caros?", a: "No. Te recomiendo productos naturales concretos para la rutina de mañana y noche, pero puedes adaptarlos a los que ya tengas." },
  { q: "¿El pago es único?", a: `Sí. Pagas ${PRICE}€ una sola vez y tienes acceso de por vida a las 4 semanas de entrenamiento y a tus rutinas.` },
  { q: "¿Para qué edad está indicado?", a: "Está especialmente diseñado para mujeres de 35 a 50 años, pero es apto para cualquier edad que quiera cuidar su rostro de forma natural." },
];

export default function Landing() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const startCheckout = async () => {
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Introduce un email válido para continuar.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        email: email.toLowerCase().trim(),
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setLoading(false);
    }
  };

  const paypalSuccess = (info) => {
    navigate("/pago-exitoso", { state: { paypal: true, email: info.email, accountExists: info.accountExists } });
  };

  const scrollToPricing = () => document.getElementById("precio")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="bg-cream text-forest overflow-x-hidden">
      {/* NAV */}
      <nav className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-cream/80 border-b border-forest/10">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <Link to="/acceder" data-testid="nav-login-link" className="text-sm font-medium text-forest/70 hover:text-forest transition-colors px-3 py-2">
              Ya tengo acceso
            </Link>
            <button data-testid="nav-cta-button" onClick={scrollToPricing} className="hidden sm:inline-flex items-center gap-2 rounded-full bg-clay text-cream text-sm font-medium px-5 py-2.5 hover:bg-clay-dark transition-colors">
              Empezar por {PRICE}€
            </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <header className="grain relative pt-28 pb-16 sm:pt-36 sm:pb-24">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial="hidden" animate="show" variants={fadeUp}>
            <span className="inline-flex items-center gap-2 rounded-full bg-sage px-4 py-1.5 text-xs tracking-[0.2em] uppercase text-forest/70 font-semibold">
              <Leaf className="w-3.5 h-3.5 text-clay" /> Yoga facial natural
            </span>
            <h1 className="mt-6 font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Tu rostro más firme y luminoso en <span className="text-clay italic">4 semanas</span>
            </h1>
            <p className="mt-6 text-lg text-forest/70 max-w-md leading-relaxed">
              El método de yoga facial pensado para mujeres de 35 a 50 años sin tiempo.
              Entrenamiento diario breve + tu rutina de mañana y noche con productos naturales.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button data-testid="hero-cta-button" onClick={scrollToPricing} className="group inline-flex items-center gap-2 rounded-full bg-clay text-cream text-base font-medium px-7 py-4 hover:bg-clay-dark hover:scale-[1.03] transition-transform">
                Empezar ahora por {PRICE}€
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <div className="flex items-center gap-2 text-sm text-forest/60">
                <ShieldCheck className="w-4 h-4 text-clay" /> Pago único · Acceso de por vida
              </div>
            </div>
            <div className="mt-8 flex items-center gap-4 text-sm text-forest/60">
              <div className="flex -space-x-1">
                {[0, 1, 2, 3].map((i) => (
                  <span key={i} className="w-7 h-7 rounded-full border-2 border-cream bg-muted-sage" />
                ))}
              </div>
              <span className="flex items-center gap-1">
                <Star className="w-4 h-4 fill-clay text-clay" /> +1.200 mujeres ya practican
              </span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="relative">
            <div className="absolute -inset-6 bg-sage blob -z-10" />
            <img src={IMAGES.hero} alt="Mujer practicando yoga facial" className="w-full rounded-[2rem] object-cover shadow-sm" />
            <div className="absolute -bottom-5 -left-4 sm:left-6 bg-cream border border-forest/10 rounded-2xl px-5 py-3 shadow-sm">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-clay" />
                <div>
                  <p className="font-serif text-2xl leading-none">8 min</p>
                  <p className="text-xs text-forest/60">al día, nada más</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </header>

      {/* TRUST BAR */}
      <section className="border-y border-forest/10 bg-sage/50">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { k: "28 días", v: "de entrenamiento" },
            { k: "Mañana + noche", v: "rutina guiada" },
            { k: "100% natural", v: "sin agujas" },
            { k: `${PRICE}€ único`, v: "sin suscripción" },
          ].map((s) => (
            <div key={s.k}>
              <p className="font-serif text-2xl text-forest">{s.k}</p>
              <p className="text-xs tracking-wide text-forest/60 uppercase mt-1">{s.v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BENEFITS */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="max-w-xl">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-clay">Por qué funciona</span>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl leading-tight">Un ritual sencillo con resultados reales</h2>
        </div>
        <div className="mt-14 grid sm:grid-cols-2 gap-6">
          {benefits.map((b, i) => (
            <motion.div key={b.title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
              className="bg-white border border-forest/10 rounded-2xl p-8 hover:-translate-y-1 transition-transform">
              <div className="w-12 h-12 rounded-full bg-sage flex items-center justify-center mb-5">
                <b.icon className="w-5 h-5 text-clay" />
              </div>
              <h3 className="font-serif text-2xl">{b.title}</h3>
              <p className="mt-2 text-forest/70 leading-relaxed">{b.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 4 WEEKS */}
      <section className="bg-forest text-cream py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="max-w-xl">
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-clay">El programa</span>
            <h2 className="mt-3 font-serif text-4xl sm:text-5xl leading-tight text-cream">4 semanas, 4 focos, un rostro transformado</h2>
            <p className="mt-4 text-cream/70">Cada semana trabajamos una zona del rostro con ejercicios progresivos y fáciles de seguir.</p>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {weeks.map((w, i) => (
              <motion.div key={w.n} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
                className="rounded-2xl border border-cream/15 bg-cream/5 p-7 hover:bg-cream/10 transition-colors">
                <span className="font-serif text-5xl text-clay">0{w.n}</span>
                <h3 className="mt-4 font-serif text-2xl text-cream">{w.theme}</h3>
                <p className="mt-2 text-sm text-cream/70">{w.focus}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT'S INSIDE + PRODUCTS */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-28 grid lg:grid-cols-2 gap-14 items-center">
        <div className="relative">
          <div className="absolute -inset-5 bg-sage blob -z-10" />
          <img src={IMAGES.products} alt="Productos naturales" className="w-full rounded-[2rem] object-cover" />
        </div>
        <div>
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-clay">Tu rutina facial</span>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl leading-tight">Mañana y noche, con mis productos naturales</h2>
          <p className="mt-4 text-forest/70 leading-relaxed">
            Además del yoga facial, te guío en tu ritual de cuidado diario con recomendaciones concretas de productos naturales para cada paso.
          </p>
          <div className="mt-8 space-y-4">
            {[
              { icon: Sun, title: "Ritual de mañana", text: "Limpieza suave, tónico, vitamina C, contorno, hidratación y protección solar." },
              { icon: Moon, title: "Ritual de noche", text: "Doble limpieza, bakuchiol natural, aceite de rosa mosqueta y masaje Gua Sha." },
            ].map((r) => (
              <div key={r.title} className="flex gap-4 bg-white border border-forest/10 rounded-2xl p-5">
                <div className="w-11 h-11 shrink-0 rounded-full bg-sage flex items-center justify-center">
                  <r.icon className="w-5 h-5 text-clay" />
                </div>
                <div>
                  <h3 className="font-serif text-xl">{r.title}</h3>
                  <p className="text-sm text-forest/70 mt-1">{r.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-sage/50 border-y border-forest/10 py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <h2 className="font-serif text-4xl sm:text-5xl text-center">Lo que dicen nuestras alumnas</h2>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.div key={t.name} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
                className="bg-white border border-forest/10 rounded-2xl p-7">
                <div className="flex gap-0.5 mb-4">
                  {[0, 1, 2, 3, 4].map((s) => <Star key={s} className="w-4 h-4 fill-clay text-clay" />)}
                </div>
                <p className="text-forest/80 leading-relaxed italic font-serif text-lg">“{t.text}”</p>
                <p className="mt-4 text-sm font-semibold text-clay">{t.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="precio" className="max-w-3xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <div className="text-center">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-clay">Acceso completo</span>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl leading-tight">Un pago único. Tuyo para siempre.</h2>
        </div>
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
          className="mt-12 bg-white border border-forest/10 rounded-3xl p-8 sm:p-10">
          <div className="flex items-end justify-center gap-2">
            <span className="font-serif text-7xl text-forest leading-none">{PRICE}€</span>
            <span className="text-forest/50 mb-2 line-through">97€</span>
          </div>
          <p className="text-center text-sm text-forest/60 mt-2">Pago único · Sin suscripción · Acceso de por vida</p>

          <ul className="mt-8 space-y-3 max-w-sm mx-auto">
            {[
              "4 semanas de entrenamiento diario (28 días)",
              "Ejercicios de yoga facial guiados paso a paso",
              "Rutina facial de mañana y noche",
              "Recomendaciones de productos naturales",
              "Seguimiento de tu progreso",
              "Acceso de por vida a todo el contenido",
            ].map((f) => (
              <li key={f} className="flex items-start gap-3 text-forest/80">
                <Check className="w-5 h-5 text-clay shrink-0 mt-0.5" /> {f}
              </li>
            ))}
          </ul>

          <div className="mt-8 max-w-sm mx-auto">
            <label className="text-xs tracking-[0.2em] uppercase font-semibold text-forest/60">Tu email</label>
            <input
              data-testid="checkout-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              className="mt-2 w-full rounded-full bg-sage/60 border border-transparent focus:border-clay focus:bg-white outline-none px-5 py-3.5 text-forest transition-colors"
            />
            <button
              data-testid="checkout-stripe-button"
              onClick={startCheckout}
              disabled={loading}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-clay text-cream font-medium px-6 py-4 hover:bg-clay-dark transition-colors disabled:opacity-60"
            >
              <CreditCard className="w-4 h-4" />
              {loading ? "Redirigiendo…" : `Pagar ${PRICE}€ con tarjeta`}
            </button>

            <div className="mt-4 flex items-center gap-3">
              <span className="h-px flex-1 bg-forest/10" />
              <span className="text-xs uppercase tracking-[0.2em] text-forest/40">o</span>
              <span className="h-px flex-1 bg-forest/10" />
            </div>

            <div className="mt-4">
              <PaypalCheckout email={email} onSuccess={paypalSuccess} />
            </div>

            <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-forest/50">
              <Lock className="w-3.5 h-3.5" /> Pago 100% seguro con Stripe y PayPal
            </p>
          </div>
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-20 sm:pb-28">
        <h2 className="font-serif text-4xl sm:text-5xl text-center">Preguntas frecuentes</h2>
        <div className="mt-12 space-y-3">
          {faqs.map((f, i) => (
            <div key={i} className="bg-white border border-forest/10 rounded-2xl overflow-hidden">
              <button
                data-testid={`faq-toggle-${i}`}
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
              >
                <span className="font-serif text-xl">{f.q}</span>
                <ChevronDown className={`w-5 h-5 text-clay shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </button>
              {openFaq === i && <p className="px-6 pb-5 text-forest/70 leading-relaxed">{f.a}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-forest text-cream/80">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
            <Logo light />
            <div className="flex items-center gap-3 text-sm">
              <HeartHandshake className="w-4 h-4 text-clay" />
              Cuidado facial natural para mujeres reales
            </div>
          </div>
          <div className="mt-10 pt-8 border-t border-cream/10 text-xs text-cream/50 flex flex-col sm:flex-row justify-between gap-3">
            <span>© {new Date().getFullYear()} Tus 10 Minutos · Yoga Facial. Todos los derechos reservados.</span>
            <span>Pago único de {PRICE}€ · Acceso de por vida</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
